import { CalendarBlank, IconWeight } from "@phosphor-icons/react";
import * as Popover from "@radix-ui/react-popover";
import classNames from "classnames/bind";
import { isValid, parse, parseISO } from "date-fns";
import addDays from "date-fns/addDays";
import addMonths from "date-fns/addMonths";
import addWeeks from "date-fns/addWeeks";
import addYears from "date-fns/addYears";
import { isPlainObject } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import { Icon } from "../../Icon";
import {
  mergeProps,
  stripInvalidPropsForElement,
} from "../../utils/component-props";
import { InputWithLabels } from "../_components/InputWithLabels";
import styles from "./DateSelector.module.scss";
import DateSelectorMenuContent from "./DateSelectorMenuContent";
import DateSelectorValue from "./DateSelectorValue";
import type {
  ControlledDateSelectorInputProps,
  DateRangeValue,
  DateSelectorProps,
  DateValue,
  DateValueOrRange,
} from "./types";
import { isRangeValue } from "./types";

const cx = classNames.bind(styles);

const nextPrevIconProps: { size: number; weight: IconWeight } = {
  size: 22,
  weight: "light",
};

// IMPROVE: this component needs a refactor. there are many code smells here.
/**
 * A component to allow for seamless selection of dates/times/datetimes/dateranges
 */
const DateSelectorInput = ({
  value,
  onChange,
  variant = "day",
  isRange = false,
  isDisabled,
  error,
  invalidMessage,
  "aria-invalid": ariaInvalid,
  isFluid,
  min,
  max,
  placeholder,
  isArraySyntax,
  hasNextPrevButtons,
  isClearable,
  leftSlot,
  flairSlot,
  style,
  className,
  name,
  id,
  ...restProps
}: DateSelectorProps) => {
  // Whether or not the menu is currently open
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const getDefaultDisplayValue = () => {
    if (isArraySyntax && Array.isArray(value) && value[0]) {
      return { from: value[0], to: value?.[1] };
    }

    if (value instanceof Date) {
      return value;
    }

    if (isRangeValue(value)) {
      return {
        from: value?.from
          ? value.from instanceof Date
            ? value.from
            : typeof value.from === "string"
              ? new Date(value.from)
              : new Date()
          : new Date(),
        to: value?.to
          ? value.to instanceof Date
            ? value.to
            : typeof value.to === "string"
              ? new Date(value.to)
              : new Date()
          : new Date(),
      };
    }

    if (typeof value === "string") {
      const parsedDate = parse(value, "yyyy-MM-dd", new Date());

      if (isValid(parsedDate)) return parsedDate;

      const parsedISODate = parseISO(value);

      if (isValid(parsedISODate)) return parsedISODate;
    }

    // in all other cases, the date isn't renderable / modifable through this component
    return;
  };

  /**
   * The display value of the component
   *
   * If the component is a range, then the display value is an object with a from and to key or undefined
   * If the component is not a range, then the display value is a Date or undefined
   *
   * Defaults to the value prop
   */
  const [displayValue, setDisplayValue] = useState<
    DateValueOrRange | Date | undefined
  >(getDefaultDisplayValue());

  // Callback to open the menu
  const onOpenMenu = () => {
    if (!isDisabled) {
      setIsMenuOpen(true);
    }
  };

  // Callback to close the menu
  const onCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const handleOnChange = (newValue: DateValueOrRange | undefined) => {
    if (!isRangeValue(newValue)) {
      onChange?.(newValue);
      setDisplayValue(newValue);
    } else {
      const newDisplayValue = { ...(displayValue ?? {}), ...newValue };

      setDisplayValue(newDisplayValue);

      if (newValue?.from && newValue?.to) {
        onChange?.(
          // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/19360
          isArraySyntax
            ? [newDisplayValue.from, newDisplayValue.to]
            : newDisplayValue
        );
      }
    }
  };

  const onDateStepChange = (stepSize: number) => {
    // default delta time function:
    let deltaTimeFunction = (input: Date, _: number) => input;

    switch (variant) {
      case "day":
        deltaTimeFunction = addDays;
        break;
      case "week":
        deltaTimeFunction = addWeeks;
        break;
      case "month":
        deltaTimeFunction = addMonths;
        break;
      case "year":
        deltaTimeFunction = addYears;
        break;
    }

    if (isRangeValue(value)) {
      const newValue: DateRangeValue = {
        from: deltaTimeFunction(value.from, stepSize),
        to: value?.to ? deltaTimeFunction(value.to, stepSize) : undefined,
      };
      handleOnChange(newValue);
    } else {
      const newValue: DateValue = deltaTimeFunction(
        // TODO: does not account for array syntax, fix it
        // @ts-expect-error this is correct and needs to be fixed
        value ?? new Date(),
        stepSize
      );
      handleOnChange(newValue);
    }
  };

  /**
   * Keeps component from crashing when switching between range and non-range
   */
  useEffect(() => {
    if (!isMenuOpen) {
      if (isRange || !["day", "time"].includes(variant)) {
        // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/19360
        if (!value?.from || !value?.to) {
          setDisplayValue(getDefaultDisplayValue());
        }
      }
    }
  }, [isMenuOpen, value, variant]);

  useEffect(() => {
    setDisplayValue(getDefaultDisplayValue());
  }, [value]);

  const shouldRenderNextPrevButtons =
    ["day", "week", "month", "year"].includes(variant) && hasNextPrevButtons;

  return (
    <div
      {...stripInvalidPropsForElement(restProps, "div")}
      className={cx("root-container", variant, className, {
        isFluid,
        isActive: isMenuOpen,
        isDisabled,
        hasError: error || invalidMessage || ariaInvalid === true,
        isRange,
      })}
      style={style}
    >
      {leftSlot?.({ className: cx("left-slot") })}

      {shouldRenderNextPrevButtons && (
        <button
          className={cx("prev-button", {
            hasLeftSlot: leftSlot,
          })}
          onClick={() => onDateStepChange(-1)}
          aria-label="Previous"
        >
          <Icon
            variant="caret-left"
            {...nextPrevIconProps}
          />
        </button>
      )}

      <input
        id={id}
        name={name}
        type="date"
        value={JSON.stringify(value)}
        hidden
      />

      <Popover.Root
        open={isMenuOpen}
        onOpenChange={(newOpen) => {
          if (!isDisabled) {
            setIsMenuOpen(newOpen);
          }
        }}
      >
        <Popover.Trigger asChild>
          <button className={cx("popover-trigger")}>
            <div
              className={cx("container", {
                hasNextPrevButtons,
                hasLeftSlot: leftSlot,
              })}
              onClick={onOpenMenu}
            >
              {/* @ts-expect-error: https://github.com/microsoft/TypeScript/issues/19360 */}
              <DateSelectorValue
                variant={variant}
                value={displayValue}
                isRange={isRange}
                placeholder={placeholder}
                isMenuOpen={isMenuOpen}
                isClearable={isClearable}
              />
              {flairSlot?.({ className: cx("flair-slot") }) ?? (
                <CalendarBlank
                  weight="thin"
                  className={cx("icon", {
                    "icon--has-error": error,
                  })}
                />
              )}
            </div>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            sideOffset={4}
            className={cx("popover-content")}
          >
            {/* @ts-expect-error: https://github.com/microsoft/TypeScript/issues/19360 */}
            <DateSelectorMenuContent
              variant={variant}
              value={displayValue}
              onChange={handleOnChange}
              isRange={isRange}
              onClose={onCloseMenu}
              min={min}
              max={max}
              isClearable={isClearable}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {shouldRenderNextPrevButtons && (
        <button
          className={cx("next-button")}
          onClick={() => onDateStepChange(1)}
          aria-label="Next"
        >
          <Icon
            variant="caret-right"
            {...nextPrevIconProps}
          />
        </button>
      )}
    </div>
  );
};

DateSelectorInput.displayName = "DateSelectorInput";

export const ControlledDateSelectorInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledDateSelectorInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    // @ts-expect-error expects a certain shape but it's fine
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value)
                        ? `${typeof label === "string" ? label : "Date input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
      disabled: field.disabled,
      name: field.name,
      ref: field.ref,
    });
  }, [
    restProps,
    field.onChange,
    field.onBlur,
    field.disabled,
    field.name,
    field.ref,
  ]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, labelId, inputId, flairSlot, leftSlot }) => (
        <DateSelectorInput
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          flairSlot={flairSlot}
          leftSlot={leftSlot}
          isInline={isInline}
          isFluid={isFluid}
          {...mergedProps}
          value={field.value}
        />
      )}
    </InputWithLabels>
  );
};

ControlledDateSelectorInput.displayName = "ControlledDateSelectorInput";

export default DateSelectorInput;
